import React from 'react';
import { Band, GroupShell, Text } from '@billda/design-system';
import { formatCurrency } from '../../../../../shared/utils/currency';
import { useSiteReimportStore } from '../../../stores/siteReimportStore';
import type { PlotDiff } from '../../../types/reimportTypes';
import ReimportLabourItemRow, {
  REIMPORT_COLLAPSE,
  SignedPriceDelta,
} from './ReimportLabourItemRow';

interface ReimportPlotRowProps {
  plot: PlotDiff;
  showUnchangedItems?: boolean;
}

export const getPlotTotals = (plot: PlotDiff) =>
  plot.labourItems.reduce(
    (totals, item) => {
      const current = item.currentPrice ?? 0;
      const imported =
        item.changeType === 'Removed' ? 0 : (item.newPrice ?? current);

      return {
        current: totals.current + current,
        imported: totals.imported + imported,
        effective: totals.effective + (item.isProtected ? current : imported),
      };
    },
    { current: 0, imported: 0, effective: 0 }
  );

const ReimportPlotRow: React.FC<ReimportPlotRowProps> = ({
  plot,
  showUnchangedItems = false,
}) => {
  const { expandedRows, toggleRowExpansion } = useSiteReimportStore();
  const isExpanded = expandedRows.has(plot.plotNumber);
  const visibleItems = showUnchangedItems
    ? plot.labourItems
    : plot.labourItems.filter(item => item.changeType !== 'Unchanged');
  const totals = getPlotTotals(plot);

  return (
    <GroupShell
      open={isExpanded}
      onToggle={() => toggleRowExpansion(plot.plotNumber)}
      header={(open, toggle) => (
        <Band
          collapse={REIMPORT_COLLAPSE}
          primary={`${plot.plotNumber} — ${plot.plotName}`}
          count={visibleItems.length}
          inline={
            <Text
              variant="caption"
              color="secondary"
              noWrap
              className="tabular-nums"
            >
              {formatCurrency(totals.current)} →{' '}
              {formatCurrency(totals.effective)}
            </Text>
          }
          inlineHiddenBelow={860}
          trailing={
            <SignedPriceDelta value={totals.effective - totals.current} />
          }
          open={open}
          onToggle={toggle}
          data-testid="reimport-plot-header"
        />
      )}
    >
      {visibleItems.map(item => (
        <ReimportLabourItemRow key={item.definitionName} item={item} />
      ))}
    </GroupShell>
  );
};

export default ReimportPlotRow;
